import React from 'react';
import { RefreshCw, AlertTriangle } from 'lucide-react';

// Detects a failed dynamic import / stale webpack chunk. After a redeploy the
// chunk filenames change (content hash), so a browser tab still running the OLD
// index.html tries to fetch a chunk that no longer exists → the lazy import
// rejects and, without a boundary, blanks the whole app to a white screen.
const isChunkLoadError = (error) => {
  const msg = String(error?.message || error || '');
  const name = String(error?.name || '');
  return (
    name === 'ChunkLoadError' ||
    /Loading chunk [\d]+ failed/i.test(msg) ||
    /Loading CSS chunk/i.test(msg) ||
    /Failed to fetch dynamically imported module/i.test(msg) ||
    /error loading dynamically imported module/i.test(msg) ||
    /'text\/html' is not a valid JavaScript MIME type/i.test(msg)
  );
};

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, isChunk: false };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error, isChunk: isChunkLoadError(error) };
  }

  componentDidCatch(error) {
    // On a stale-chunk error, force ONE hard reload so the browser pulls the
    // freshly-deployed chunks. A sessionStorage guard prevents a reload loop
    // if the reload itself fails for some other reason.
    if (isChunkLoadError(error)) {
      const guardKey = 'chunk_reload_attempted';
      if (!sessionStorage.getItem(guardKey)) {
        sessionStorage.setItem(guardKey, String(Date.now()));
        window.location.reload();
        return;
      }
    }
    // eslint-disable-next-line no-console
    console.error('[ErrorBoundary] caught render error:', error);
  }

  handleReload = () => {
    sessionStorage.removeItem('chunk_reload_attempted');
    window.location.reload();
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    // A chunk error mid-reload — show a lightweight spinner while the page
    // reloads to fetch the new build.
    if (this.state.isChunk) {
      return (
        <div data-testid="chunk-reload-screen" className="min-h-screen bg-white flex items-center justify-center">
          <div className="text-center">
            <div className="w-10 h-10 border-3 border-amber-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
            <p className="text-sm text-slate-500">Updating to the latest version…</p>
          </div>
        </div>
      );
    }

    return (
      <div data-testid="error-boundary-screen" className="min-h-screen bg-neutral-50 flex items-center justify-center px-6">
        <div className="max-w-md w-full text-center bg-white border border-slate-200 rounded-2xl shadow-sm p-8">
          <div className="w-14 h-14 rounded-2xl bg-amber-100 text-amber-700 flex items-center justify-center mx-auto mb-4">
            <AlertTriangle className="w-7 h-7" strokeWidth={1.7} />
          </div>
          <h2 className="text-lg font-bold text-slate-900 mb-1">Something went wrong on this page</h2>
          <p className="text-sm text-slate-500 mb-5">
            The page hit an unexpected error and couldn't render. Reloading usually fixes it. If it keeps happening, let the team know.
          </p>
          <button
            data-testid="error-boundary-reload-btn"
            onClick={this.handleReload}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-amber-500 hover:bg-amber-600 text-white font-semibold text-sm transition-colors"
          >
            <RefreshCw className="w-4 h-4" /> Reload page
          </button>
          {this.state.error?.message && (
            <p className="mt-4 text-[11px] text-slate-400 font-mono break-all">{String(this.state.error.message).slice(0, 200)}</p>
          )}
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
